import React, { createContext, useContext, useState, useEffect, useCallback} from 'react';
import NewsJson from './newsData.json';
import {fetchRequestNewsViews} from "../../api/news/newsApi"
import {processNewsData} from './News'

const NewsContext = createContext();

export const NewsProvider = ({ children }) => {
    const [newsData, setNewsData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // Функция для загрузки просмотров с таймаутом
    const fetchViewsWithTimeout = async (timeoutMs = 3000) => {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

            const viewsData = await fetchRequestNewsViews({ signal: controller.signal });
            clearTimeout(timeoutId);
            return viewsData;
        } catch (error) {
            if (error.name === 'AbortError') {
                console.warn('Views request timed out after', timeoutMs, 'ms');
            } else {
                console.error('Failed to fetch news views', error);
            }
            return []; // при ошибке или таймауте возвращаем пустой массив
        }
    };

    // Загрузка новостей (без ожидания просмотров)
    const loadNews = useCallback(async () => {
        setIsLoading(true);
        try {
            // 1. Сразу показываем новости с views = 0
            const initialNews = processNewsData(NewsJson);
            setNewsData(initialNews);
            setIsLoading(false); // новости уже отображаются

            // 2. Асинхронно подгружаем просмотры (не блокируя UI)
            const viewsData = await fetchViewsWithTimeout(3000); // таймаут 3 сек
            if (viewsData.length > 0) {
                const viewsMap = new Map();
                viewsData.forEach(item => {
                    viewsMap.set(item.article_title, item.views_count);
                });

                // Обновляем новости, добавляя реальные просмотры
                setNewsData(prevNews =>
                    prevNews.map(news => ({
                        ...news,
                        views: viewsMap.get(news.title) ?? news.views
                    }))
                );
            }
        } catch (error) {
            console.error('Error loading news:', error);
            setIsLoading(false);
        }
    }, []);

    const refreshNews = useCallback(async () => {
        await loadNews();
    }, [loadNews]);

    const updateNewsViews = useCallback((newsTitle, newViews) => {
        setNewsData(prevNews =>
            prevNews.map(news =>
                news.title === newsTitle ? { ...news, views: newViews } : news
            )
        );
    }, []);

    useEffect(() => {
        loadNews();
    }, [loadNews]);

    return (
        <NewsContext.Provider value={{
            newsData,
            isLoading,
            updateNewsViews,
            refreshNews
        }}>
            {children}
        </NewsContext.Provider>
    );
};

export const useNews = () => {
    const context = useContext(NewsContext);
    if (!context) {
        throw new Error('useNews must be used within a NewsProvider');
    }
    return context;
};